/**
 * chat.ts — Chat REST API helpers
 *
 * All functions require a JWT token from AuthContext.
 * Base URL is set via REACT_APP_API_URL env variable (default: http://localhost:5000/api).
 *
 * Endpoints used:
 *   GET  /chat/messages/:userId       — paginated messages with a specific user
 *   POST /chat/messages               — send text or file message
 *   PUT  /chat/messages/:id/edit      — edit a message (backend enforces 30s window)
 *   PUT  /chat/conversations/:id/read — mark conversation as read
 */

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

/** Returns JSON headers with Authorization bearer token */
const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

/** Fetch paginated messages between logged-in user and userId. Page 1 = most recent 50. */
export async function fetchMessages(token: string, userId: string, page = 1) {
  const res = await fetch(`${BASE}/chat/messages/${userId}?page=${page}&limit=50`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to fetch messages");
  return res.json();
}

/**
 * Send a text or file message to receiverId.
 * If file is provided, sends as multipart/form-data with messageType="file".
 * Otherwise sends JSON with messageType="text".
 */
export async function sendMessageApi(token: string, receiverId: string, content: string, file?: File) {
  if (file) {
    const form = new FormData();
    form.append("receiverId", receiverId);
    form.append("encryptedContent", content || file.name);
    form.append("messageType", "file");
    form.append("file", file);
    const res = await fetch(`${BASE}/chat/messages`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) throw new Error("Failed to send message");
    return res.json();
  }
  const res = await fetch(`${BASE}/chat/messages`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ receiverId, encryptedContent: content, messageType: "text" }),
  });
  if (!res.ok) throw new Error("Failed to send message");
  return res.json();
}

/**
 * Edit an existing message by ID.
 * Backend enforces a 30-second edit window — throws error if expired.
 */
export async function editMessageApi(token: string, messageId: string, content: string) {
  const res = await fetch(`${BASE}/chat/messages/${messageId}/edit`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ encryptedContent: content }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to edit message");
  return data;
}

/** Mark all messages in a conversation with userId as read for the logged-in user */
export async function markAsRead(token: string, userId: string) {
  await fetch(`${BASE}/chat/conversations/${userId}/read`, {
    method: "PUT",
    headers: authHeaders(token),
  });
}

/** Download a file from GridFS with auth token — triggers browser download */
export async function downloadFile(token: string, fileId: string, fileName: string) {
  const res = await fetch(`${BASE}/chat/files/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download file");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Fetch teams assigned to the logged-in mentor */
export async function fetchAssignedTeams(token: string) {
  const res = await fetch(`${BASE}/teams/assigned`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error("Failed to fetch assigned teams");
  return res.json(); // Array of team objects with populated leader
}

/** Fetch all teams the logged-in user is part of (member or leader) with mentor info for chat */
export async function fetchUserTeams(token: string) {
  const res = await fetch(`${BASE}/teams/`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error("Failed to fetch user teams");
  return res.json(); // Array of team objects with mentorId and hackathonId populated
}

/** Fetch the mentor assigned to the logged-in user's team */
export async function fetchAssignedMentor(token: string) {
  const res = await fetch(`${BASE}/teams/my-mentor`, { headers: authHeaders(token) });
  if (!res.ok) return null;
  return res.json(); // { _id, username, name, isOnline }
}
